import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { X, Plus } from '@phosphor-icons/react';
import noImage from '../assets/no-image.png';
import { colors, typography } from '../theme/tokens';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const Overlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0,0,0,0.2);
  z-index: 1000;
  display: ${({ open }) => (open ? 'block' : 'none')};
`;

const Drawer = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 30vw;
  min-width: 320px;
  max-width: 480px;
  height: 100vh;
  background: ${colors.neutral.white};
  box-shadow: -2px 0 8px rgba(0,0,0,0.08);
  z-index: 1100;
  transform: translateX(${({ open }) => (open ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
`;

const Title = styled.div`
  font-family: ${typography.label.large.fontFamily};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.brand.laurel};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.neutral.charcoal};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background 0.2s;
  &:hover {
    background: ${colors.neutral.offWhite};
  }
`;

const SearchBar = styled.input`
  width: 90%;
  margin: 1.5rem auto 1rem auto;
  display: block;
  padding: 0.75rem 1rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 6px;
  font-size: 1rem;
  background: ${colors.neutral.white};
  color: ${colors.neutral.black};
  font-family: ${typography.paragraph.medium.fontFamily};
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
  }
`;

const RecipeList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 2rem 2rem 2rem;
`;

const RecipeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${colors.neutral.offWhite};
`;

const Thumbnail = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  background: ${colors.neutral.lightCharcoal};
`;

const RecipeTitle = styled.div`
  flex: 1;
  font-size: 1rem;
  color: ${colors.neutral.darkCharcoal};
  font-family: ${typography.paragraph.medium.fontFamily};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AddButton = styled.button<{ disabled?: boolean }>`
  background: ${colors.brand.laurel};
  color: ${colors.neutral.white};
  border: none;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1rem;
  font-family: ${typography.label.medium.fontFamily};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: background 0.2s;
  &:hover {
    background: ${({ disabled }) => (disabled ? colors.brand.laurel : colors.brand.laurelDark)};
  }
`;

const Loading = styled.div`
  text-align: center;
  color: ${colors.neutral.charcoal};
  padding: 1rem 0;
`;

const Empty = styled.div`
  text-align: center;
  color: ${colors.neutral.charcoal};
  padding: 2rem 0;
`;

interface Recipe {
  RecipeId: number;
  Name: string;
  ImageURL: string;
}

interface RecipePickerDrawerProps {
  open: boolean;
  onClose: () => void;
  meal: any;
  onRecipeSelect: (recipeId: number) => void;
  loadingRecipeId: number | null;
}

const RecipePickerDrawer: React.FC<RecipePickerDrawerProps> = ({ open, onClose, meal, onRecipeSelect, loadingRecipeId }) => {
  if (!open || !meal) return null;
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const pageSize = 20;

  // Debug: Log when drawer opens and meal prop
  useEffect(() => {
    if (open) {
      console.debug('[RecipePickerDrawer] Drawer opened for meal:', meal);
    }
  }, [open, meal]);

  // Fetch recipes logic (infinite scroll, search)
  const fetchRecipes = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    if (!user?.id) return;

    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        console.error('[RecipePickerDrawer] Error fetching creator details:', creatorError);
        return;
      }

      if (!creatorDetails) {
        console.error('[RecipePickerDrawer] No creator details found for user');
        return;
      }

      const creatorId = creatorDetails.id;

      let query = supabase
        .from('Recipes')
        .select('RecipeId, Name, ImageURL')
        .eq('UserID', creatorId)
        .order('DateCreated', { ascending: false });
      if (search) {
        query = query.ilike('Name', `%${search}%`);
      }
      console.debug('[RecipePickerDrawer] Fetching recipes', { pageNum, search, creatorId });
      const { data, error } = await query.range((pageNum - 1) * pageSize, pageNum * pageSize - 1);
      if (error) {
        console.error('[RecipePickerDrawer] Supabase error:', error);
        throw error;
      }
      if (data) {
        if (append) setRecipes(prev => [...prev, ...data]);
        else setRecipes(data);
        setHasMore(data.length === pageSize);
        console.debug('[RecipePickerDrawer] Recipes fetched:', data.length);
      }
    } catch (err) {
      // Optionally handle error
      console.error('[RecipePickerDrawer] Error fetching recipes:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [search, user?.id]);

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchRecipes(1);
    }
  }, [open, fetchRecipes]);

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchRecipes(1);
    }
  }, [search, open, fetchRecipes]);

  // Infinite scroll
  const handleScroll = () => {
    if (!listRef.current || isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      console.debug('[RecipePickerDrawer] Infinite scroll, loading page:', nextPage);
      fetchRecipes(nextPage, true);
    }
  };

  useEffect(() => {
    const ref = listRef.current;
    if (!ref) return;
    ref.addEventListener('scroll', handleScroll);
    return () => ref.removeEventListener('scroll', handleScroll);
  });

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Close on outside click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Drawer title
  const week = meal?.week_number ? `Week ${meal.week_number}` : '';
  const day = meal?.day_of_week ? `Day ${meal.day_of_week}` : '';
  const title = [week, day].filter(Boolean).join(' • ');

  // Debug: Log when add button is clicked
  const handleAddClick = (recipeId: number) => {
    console.debug('[RecipePickerDrawer] Add button clicked for recipeId:', recipeId, 'meal:', meal);
    onRecipeSelect(recipeId);
  };

  return (
    <Overlay open={open} onClick={handleOverlayClick}>
      <Drawer open={open}>
        <Header>
          <Title>Pick a Recipe {title && <span style={{ color: colors.neutral.charcoal, fontWeight: 400, fontSize: '1rem', marginLeft: 8 }}>{title}</span>}</Title>
          <CloseButton onClick={onClose}><X size={24} /></CloseButton>
        </Header>
        <SearchBar
          type="text"
          placeholder="Search recipes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <RecipeList ref={listRef}>
          {isLoading ? (
            <Loading>Loading recipes...</Loading>
          ) : recipes.length === 0 ? (
            <Empty>No recipes found.</Empty>
          ) : (
            recipes.map(recipe => (
              <RecipeRow key={recipe.RecipeId}>
                <Thumbnail src={recipe.ImageURL || noImage} alt={recipe.Name} />
                <RecipeTitle>{recipe.Name}</RecipeTitle>
                <AddButton
                  disabled={meal.recipe_id === recipe.RecipeId || loadingRecipeId === recipe.RecipeId}
                  onClick={() => handleAddClick(recipe.RecipeId)}
                >
                  {loadingRecipeId === recipe.RecipeId ? (
                    <span className="spinner" style={{ width: 18, height: 18, border: '2px solid #fff', borderTop: `2px solid ${colors.brand.laurelDark}`, borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                  ) : meal.recipe_id === recipe.RecipeId ? 'Added' : <><Plus size={18} /> Add</>}
                </AddButton>
              </RecipeRow>
            ))
          )}
          {isLoadingMore && <Loading>Loading more...</Loading>}
        </RecipeList>
      </Drawer>
    </Overlay>
  );
};

export default RecipePickerDrawer; 